import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { formatTimeRu, toDateInputValue } from "../../../utils/appointmentLabels";
import {
  scheduleBranchList,
  pickDefaultBranchId,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import s from "../clinic.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

function weekRange(baseDate) {
  const d = new Date(`${baseDate}T12:00:00`);
  const day = d.getDay();
  const monday = new Date(d);
  monday.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const x = new Date(monday);
    x.setDate(monday.getDate() + i);
    days.push(toDateInputValue(x));
  }
  return days;
}

function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() + n);
  return toDateInputValue(d);
}

function sessionTitle(session) {
  if (session.title?.trim()) return session.title;
  if (session.group?.name) return session.group.name;
  if (session.specialization?.name) return session.specialization.name;
  return session.session_kind === "event" ? "Событие" : "Занятие";
}

export default function AdminDoctorCalendar({ doctorId, doctorBranchOptions }) {
  const toast = useToast();
  const today = toDateInputValue();
  const [weekStart, setWeekStart] = useState(() => weekRange(today)[0]);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const days = useMemo(() => weekRange(weekStart), [weekStart]);

  const reload = () => {
    if (!doctorId || !days.length) return;
    setLoading(true);
    clinicAPI
      .getTrainingSessions({
        doctor_id: doctorId,
        from: days[0],
        to: days[6],
      })
      .then((res) => setSessions(res.sessions || []))
      .catch((e) => {
        toast.error(e.response?.data?.error || "Не удалось загрузить календарь");
        setSessions([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const applyBranches = (options) => {
      const list = scheduleBranchList(options);
      setBranches(list);
      setBranchId((prev) =>
        list.some((b) => b.id === prev) ? prev : pickDefaultBranchId(list),
      );
    };

    if (doctorBranchOptions?.length) {
      applyBranches(doctorBranchOptions);
      return;
    }
    if (!doctorId) return;

    clinicAPI
      .getDoctor(doctorId)
      .then((r) => applyBranches(doctorBranchOptionsFromResponse(r.doctor)))
      .catch(() => toast.error("Не удалось загрузить залы тренера"));
  }, [doctorId, doctorBranchOptions, toast]);

  useEffect(() => {
    reload();
  }, [doctorId, days]);

  const shiftWeek = (delta) => {
    setWeekStart((prev) => addDays(prev, delta));
  };

  const dayLabel = (iso) => {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString("ru-RU", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  };

  const visibleSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          session.status !== "cancelled" &&
          (!branchId ||
            session.branch_id === branchId ||
            session.branch?.id === branchId),
      ),
    [sessions, branchId],
  );

  const sessionsByDay = (iso) =>
    visibleSessions.filter((session) => {
      const day = session.start_datetime
        ? toDateInputValue(new Date(session.start_datetime))
        : null;
      return day === iso;
    });

  return (
    <div>
      <div className={s.calendarToolbar}>
        {branches.length > 1 ? (
          <Field label="Зал">
            <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Все</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Неделя с">
          <Input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(e.target.value)}
          />
        </Field>
      </div>

      <div className={s.doctorWeekNav}>
        <button
          type="button"
          className={s.doctorWeekNavBtn}
          onClick={() => shiftWeek(-7)}
          aria-label="Предыдущая неделя"
        >
          ‹
        </button>
        <span className={s.doctorWeekNavLabel}>
          {dayLabel(days[0])} — {dayLabel(days[6])}
        </span>
        <button
          type="button"
          className={s.doctorWeekNavBtn}
          onClick={() => shiftWeek(7)}
          aria-label="Следующая неделя"
        >
          ›
        </button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setWeekStart(weekRange(today)[0])}
        >
          Сегодня
        </Button>
      </div>

      {loading ? (
        <p className={s.timelineLoading}>Загрузка занятий…</p>
      ) : (
        <div className={s.doctorWeekGrid}>
          {days.map((iso) => {
            const daySessions = sessionsByDay(iso);
            return (
              <div key={iso} className={s.doctorWeekDay}>
                <div className={s.doctorWeekDayHead}>
                  <span className={s.doctorWeekDayLabel}>{dayLabel(iso)}</span>
                  {daySessions.length > 0 ? (
                    <span
                      className={s.doctorWeekDayCount}
                      title={`${daySessions.length} занятий`}
                    >
                      {daySessions.length}
                    </span>
                  ) : null}
                </div>
                {daySessions.map((session) => (
                  <Link
                    key={session.id}
                    to={`/manage/app/schedule?trainer=${doctorId}&view=day&date=${iso}&session=${session.id}`}
                    className={s.calAppt}
                  >
                    <div className={s.calApptTime}>
                      {formatTimeRu(session.start_datetime)}
                    </div>
                    <span>{sessionTitle(session)}</span>
                    {session.branch?.name ? (
                      <span className={s.calApptService}>{session.branch.name}</span>
                    ) : null}
                  </Link>
                ))}
                {!daySessions.length ? (
                  <span className={s.calEmpty}>—</span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <p className={s.calHint}>
        Занятия появляются из расписания групп, назначенных тренеру. Настройте
        слоты во вкладке «Слоты» или в{" "}
        <Link to="/manage/app/schedule">календаре клуба</Link>.
      </p>
    </div>
  );
}
